import { useState } from 'react'
import type { JSX } from 'react'

import {
  BotIcon,
  Card,
  DropdownMenu,
  EllipsisVerticalIcon,
  Flex,
  Heading,
  IconButton,
  Text,
  TrashIcon,
} from '@buffer-mono/popcorn'

import { useDateTimeFormatter } from '~publish/hooks/useDateTimeFormatter'

import { useConnectedApps } from '../../../shared/hooks/useConnectedApps'
import type { ConnectedApp } from '../../../shared/hooks/useConnectedApps'
import { useRevokeApp } from '../../../shared/hooks/useRevokeApp'
import { matchClientName } from '../../../shared/integrations/matchClientName'
import { RevokeConnectionDialog } from '../RevokeConnectionDialog'
import { ScopesDisclosure } from '../ScopesDisclosure'

import styles from './ConnectionsSection.module.css'

/**
 * Repeat authorizations of one app are indistinguishable by name, so they are
 * kept adjacent rather than scattered down the list. The client id settles
 * exact ties: the query behind the list is unsorted, so without a total order
 * two connections made in the same instant could swap places between refetches.
 */
const byNameThenNewest = (a: ConnectedApp, b: ConnectedApp): number =>
  a.name.localeCompare(b.name) ||
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime() ||
  a.clientId.localeCompare(b.clientId)

export const ConnectionsSection = (): JSX.Element | null => {
  const { connectedApps } = useConnectedApps()
  const { revokeAccessConnectedApp } = useRevokeApp()
  const formatDateTime = useDateTimeFormatter()
  const [connectionToRevoke, setConnectionToRevoke] =
    useState<ConnectedApp | null>(null)
  // Not the hook's mutation loading flag: that one is shared by every row and
  // drops before the refetch that takes the revoked row off the list. A set
  // rather than one id, so revoking a second row cannot orphan the first row's
  // entry while its request is still open.
  const [revokingClientIds, setRevokingClientIds] = useState<
    ReadonlySet<string>
  >(() => new Set())

  const connections = connectedApps
    .filter((app) => app.category === 'mcp')
    .toSorted(byNameThenNewest)

  if (connections.length === 0) {
    return null
  }

  const formatConnectedAt = (createdAt: string): string =>
    formatDateTime(createdAt, {
      dateFormat: "MMM d, yyyy 'at'",
      includeTimeZone: false,
    })

  const handleRevokeConfirm = async (): Promise<void> => {
    const connection = connectionToRevoke

    if (!connection || revokingClientIds.has(connection.clientId)) {
      return
    }

    setRevokingClientIds((current) => new Set(current).add(connection.clientId))

    try {
      await revokeAccessConnectedApp({ clientId: connection.clientId })
    } finally {
      setRevokingClientIds((current) => {
        const remaining = new Set(current)
        remaining.delete(connection.clientId)
        return remaining
      })

      // Only closes the dialog it opened, so a revoke started for one
      // connection cannot dismiss a dialog the user has since opened for
      // another.
      setConnectionToRevoke((current) =>
        current?.clientId === connection.clientId ? null : current,
      )
    }
  }

  return (
    <Flex direction="column" align="stretch" gap="md" fullWidth>
      <div className={styles.header}>
        <Heading as="h2" size="sm">
          Active Integrations
        </Heading>
      </div>

      <ul className={styles.list}>
        {connections.map((connection) => {
          const integration = matchClientName(connection.name)
          const connectedAt = formatConnectedAt(connection.createdAt)
          // The name repeats across rows, so the time is what tells one row's
          // controls from another's.
          const subject = `${connection.name} connected ${connectedAt}`

          return (
            <li key={connection.clientId}>
              <Card padding="none">
                <div className={styles.connectionRow}>
                  <div className={styles.iconBadge}>
                    {integration?.iconSrc ? (
                      <img
                        src={integration.iconSrc}
                        alt=""
                        className={styles.iconImage}
                      />
                    ) : (
                      <BotIcon size="medium" />
                    )}
                  </div>

                  <div className={styles.content}>
                    <Text className={styles.connectionName}>
                      {connection.name}
                    </Text>
                    <Text className={styles.connectionDate}>
                      Connected {connectedAt}
                    </Text>
                    <ScopesDisclosure
                      scopes={connection.scopes}
                      subject={subject}
                    />
                  </div>

                  <div className={styles.actions}>
                    <DropdownMenu>
                      <DropdownMenu.Trigger>
                        <IconButton
                          label={`More actions for ${subject}`}
                          variant="tertiary"
                        >
                          <EllipsisVerticalIcon />
                        </IconButton>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Content>
                        <DropdownMenu.Item
                          onSelect={(): void =>
                            setConnectionToRevoke(connection)
                          }
                          variant="critical"
                        >
                          <TrashIcon />
                          Revoke
                        </DropdownMenu.Item>
                      </DropdownMenu.Content>
                    </DropdownMenu>
                  </div>
                </div>
              </Card>
            </li>
          )
        })}
      </ul>

      {connectionToRevoke && (
        <RevokeConnectionDialog
          appName={connectionToRevoke.name}
          connectedDate={formatConnectedAt(connectionToRevoke.createdAt)}
          open
          onOpenChange={(nextOpen): void => {
            if (!nextOpen) {
              setConnectionToRevoke(null)
            }
          }}
          onConfirm={(): void => {
            void handleRevokeConfirm()
          }}
          isLoading={revokingClientIds.has(connectionToRevoke.clientId)}
        />
      )}
    </Flex>
  )
}
